import React, { useState, useEffect } from "react";
import { SchoolItem } from "./SchoolItem";
import supabase from "../../../utils/supabase";
import { Plus } from "lucide-react";
import AddSchoolModal from "../../modals/AddSchoolModal";
import AddTeacherModal from "../../modals/AddTeacherModal";
import ConfirmDeleteSchoolModal from "../../modals/ConfirmDeleteSchoolModal";
import EditSchoolModal from "../../modals/EditSchoolModal";

interface School {
  id: string;
  name: string;
  user_id: string;
  created_at: string;
  district_id: string;
}

interface Profile {
  id: string;
  fullname: string;
}

interface DistrictOption {
  id: string;
  name: string;
}

interface SchoolOption {
  id: string;
  name: string;
  district_id: string;
}

export function Schools() {
  const [schools, setSchools] = useState<School[]>([]);
  const [profiles, setProfiles] = useState<Record<string, Profile>>({});
  const [districts, setDistricts] = useState<DistrictOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddSchoolOpen, setIsAddSchoolOpen] = useState(false);
  const [isAddTeacherOpen, setIsAddTeacherOpen] = useState(false);
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [teachersCounts, setTeachersCounts] = useState<Record<string, number>>(
    {}
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [schoolToDelete, setSchoolToDelete] = useState<School | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [schoolToEdit, setSchoolToEdit] = useState<School | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  // Create a map for district id to name
  const districtMap: Record<string, string> = {};
  districts.forEach((d) => {
    districtMap[d.id] = d.name;
  });

  async function fetchSchools(search?: string) {
    setIsLoading(true);
    try {
      let query = supabase
        .from("schools")
        .select("*")
        .order("created_at", { ascending: false });

      if (search && search.trim()) {
        query = query.ilike("name", `%${search.trim()}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error("Failed to fetch schools:", error);
        return;
      }

      setSchools(data || []);

      // Fetch profiles for all school creators
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map((s) => s.user_id))];
        await fetchProfiles(userIds);
        await fetchTeacherCounts(data.map((s) => s.id));
      }
    } catch (error) {
      console.error("Error fetching schools:", error);
    } finally {
      setIsLoading(false);
    }
  }

  async function fetchProfiles(userIds: string[]) {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, fullname")
        .in("id", userIds);

      if (error) {
        console.error("Failed to fetch profiles:", error);
        return;
      }

      const profilesMap: Record<string, Profile> = {};
      data?.forEach((profile) => {
        profilesMap[profile.id] = profile;
      });
      setProfiles(profilesMap);
    } catch (error) {
      console.error("Error fetching profiles:", error);
    }
  }

  async function fetchDistricts() {
    try {
      const { data, error } = await supabase
        .from("districts")
        .select("id, name");
      if (error) {
        console.error("Failed to fetch districts:", error);
        return;
      }
      setDistricts(data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  }

  async function fetchTeacherCounts(schoolIds: string[]) {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("school, role")
        .in("school", schoolIds)
        .eq("role", "teacher");
      if (error) {
        console.error("Failed to fetch teacher counts:", error);
        return;
      }
      const counts: Record<string, number> = {};
      schoolIds.forEach((id) => {
        counts[id] = 0;
      });
      data?.forEach((profile) => {
        if (profile.school) {
          counts[profile.school] = (counts[profile.school] || 0) + 1;
        }
      });
      setTeachersCounts(counts);
    } catch (error) {
      console.error("Error fetching teacher counts:", error);
    }
  }

  async function updateTeacherCountForSchool(schoolId: string) {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("school, role")
        .eq("school", schoolId)
        .eq("role", "teacher");
      if (error) {
        console.error("Failed to fetch teacher count for school:", error);
        return;
      }
      setTeachersCounts((prev) => ({
        ...prev,
        [schoolId]: data?.length || 0,
      }));
    } catch (error) {
      console.error("Error updating teacher count for school:", error);
    }
  }

  useEffect(() => {
    fetchDistricts();
  }, []);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(
      () => {
        fetchSchools(searchTerm);
      },
      searchTerm ? 500 : 0
    ); // No delay for initial load, 500ms delay for search

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleAddTeacher = (school: School) => {
    setSelectedSchool(school);
    setIsAddTeacherOpen(true);
  };

  const handleDeleteClick = (school: School) => {
    setSchoolToDelete(school);
    setIsDeleteModalOpen(true);
  };

  const handleEditClick = (school: School) => {
    setSchoolToEdit(school);
    setIsEditModalOpen(true);
  };

  const handleSchoolDeleted = () => {
    // Refresh the schools list
    fetchSchools(searchTerm);
  };

  const handleSchoolEdited = () => {
    // Refresh the schools list
    fetchSchools(searchTerm);
  };

  const handleTeacherAdded = (schoolId: string) => {
    updateTeacherCountForSchool(schoolId);
  };

  return (
    <div>
      {/* Add School Modal */}
      <AddSchoolModal
        isOpen={isAddSchoolOpen}
        onClose={() => setIsAddSchoolOpen(false)}
        districts={districts}
        title="Add New School"
        onSuccess={() => {
          setIsAddSchoolOpen(false);
          fetchSchools(searchTerm);
        }}
      />
      {/* Add Teacher Modal */}
      <AddTeacherModal
        isOpen={isAddTeacherOpen}
        onClose={() => {
          setIsAddTeacherOpen(false);
          setSelectedSchool(null);
        }}
        title={
          selectedSchool
            ? `Add Teacher to ${selectedSchool.name}`
            : "Add Teacher"
        }
        schoolId={selectedSchool?.id}
        districtId={selectedSchool?.district_id}
        schools={schools.map((s) => ({
          id: s.id,
          name: s.name,
          district_id: s.district_id,
        }))}
        onSuccess={() => {
          setIsAddTeacherOpen(false);
          setSelectedSchool(null);
          if (selectedSchool?.id) handleTeacherAdded(selectedSchool.id);
        }}
      />

      <ConfirmDeleteSchoolModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSchoolToDelete(null);
        }}
        school={schoolToDelete}
        onSchoolDeleted={handleSchoolDeleted}
      />

      <EditSchoolModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSchoolToEdit(null);
        }}
        school={schoolToEdit}
        onSuccess={handleSchoolEdited}
      />
      {/* Header */}
      <div className="bg-white border-b shadow-sm p-6 flex justify-between gap-5 items-start lg:items-center flex-col lg:flex-row">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Schools</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage schools across the platform.
          </p>
        </div>
        <div className="space-x-2 flex gap-2">
          <button
            className="btn btn-small"
            onClick={() => setIsAddSchoolOpen(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add School
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 bg-gray-50">
        {/* Filters */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Search Schools
              </label>
              <input
                type="text"
                className="w-full p-2 border rounded-lg"
                placeholder="Search by school name"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    District
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Teachers
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center text-sm text-gray-500 py-6 italic"
                    >
                      Loading schools...
                    </td>
                  </tr>
                ) : schools.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="text-center text-sm text-gray-500 py-6"
                    >
                      {searchTerm
                        ? "No schools found matching your search."
                        : "No schools found. Add your first school to get started."}
                    </td>
                  </tr>
                ) : (
                  schools.map((school) => (
                    <SchoolItem
                      key={school.id}
                      school={school}
                      creatorName={
                        profiles[school.user_id]?.fullname || "Unknown User"
                      }
                      districtName={
                        districtMap[school.district_id] || "Unknown District"
                      }
                      teachersCount={teachersCounts[school.id] || 0}
                      onAddTeacher={handleAddTeacher}
                      onEdit={handleEditClick}
                      onDelete={handleDeleteClick}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
